import { AnyObject } from '@feedyou/utils'
import {
  BotFeatures,
  LinkStepSchema,
  StepSchema,
  StepType,
  UploadStepSchema
} from '@feedyou/schema'

import { StepService, SubstepType } from '../../types'
import { createStepService } from './createStepServices'
import { aiStepServices } from '../../Ai/stepService'
import { dialogStepService } from '../../Dialog/stepService'
import { promptStepService } from '../../Prompt/stepService'
import { switchStepService } from '../../Switch/stepService'
import { timeoutStepService } from '../../Timeout/stepService'
import { storageStepService } from '../../Storage/stepService'
import { postponeStepService } from '../../Postpone/stepService'
import { emailStepService } from '../../Email/stepService'
import { actionStepService } from '../../Action/stepService'
import { messageStepService } from '../../Message/stepService'
import { noteStepServices } from '../../Note/stepService'
import { attachmentStepService } from '../../Attachment/stepService'
import { integrationStepService } from '../../IntegrationStep/stepService'
import { questionStepService } from '../../Question/stepService'

const STEP_SERVICES = {
  ai: aiStepServices,
  action: actionStepService,
  attachment: attachmentStepService,
  dialog: dialogStepService,
  email: emailStepService,
  note: noteStepServices,
  integration: integrationStepService,
  link: createStepService<LinkStepSchema, {}>({}),
  message: messageStepService,
  postpone: postponeStepService,
  prompt: promptStepService,
  question: questionStepService,
  storage: storageStepService,
  switch: switchStepService,
  timeout: timeoutStepService,
  upload: createStepService<UploadStepSchema, {}>({})
} as const

function getStepService<T extends StepType>(type: T) {
  return STEP_SERVICES[type] ?? createStepService<StepSchema<T>, {}>({}) // provide fallback to prevent dg crashes for non-existent step type
}

export function getUsedDialogs(step: StepSchema, enabledOnly?: boolean) {
  // @ts-ignore
  return getStepService(step.type).getUsedDialogs?.(step, enabledOnly)
}

export function searchStep(step: StepSchema, query: string) {
  // @ts-ignore
  return getStepService(step.type).search?.(step, query)
}

export function unsetDialogs(step: StepSchema, dialogIds: string[]) {
  // @ts-ignore
  return getStepService(step.type).unsetDialogs?.(dialogIds, step)
}

export function getSubdialogConnectionsMetadata(step: StepSchema, parentDialogId: string) {
  // @ts-ignore
  return getStepService(step.type).getSubdialogConnectionsMetadata?.(step, parentDialogId)
}

export function useStepFormSchema<Step extends StepSchema>(step: Step, sdkFeatures: BotFeatures[]) {
  return (STEP_SERVICES[step.type] as StepService<StepSchema, {}>)?.useFormSchema(sdkFeatures)
}

export function useStepFormUiSchema(step: StepSchema, sdkFeatures: BotFeatures[]) {
  return (STEP_SERVICES[step.type] as StepService<StepSchema, {}>)?.useFormUiSchema(
    step,
    sdkFeatures
  )
}

export function useAdjustedStepFormData(step: StepSchema) {
  return (STEP_SERVICES[step.type] as StepService<StepSchema, {}>)?.useAdjustedFormData!()
}

export function useSubstepFormSchema<Step extends StepSchema>(
  step: Step,
  sdkFeatures: BotFeatures[]
) {
  const stepService = STEP_SERVICES[step.type] as StepService<StepSchema, {}>
  return findSubstep(stepService)?.useFormSchema(sdkFeatures)
}

export function useSubstepFormUiSchema(step: StepSchema, sdkFeatures: BotFeatures[]) {
  const stepService = STEP_SERVICES[step.type] as StepService<StepSchema, {}>
  return findSubstep(stepService)?.useFormUiSchema(step, sdkFeatures)
}

function findSubstep(stepService: StepService<StepSchema, AnyObject>) {
  const substeps = stepService?.substeps || {}
  const substeTypes = Object.keys(substeps) as SubstepType[]
  const substeType = substeTypes.filter(type => !!substeps?.[type])?.[0]

  return substeps[substeType]
}
